// src/components/report/manifestTracking/ManifestTrackingTable.js
import React from 'react';
import {
    Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';

import EmptyState from '../../gerencia/report/orderService/components/EmptyState';
import {
    formatDate, formatDateTime, formatManifestCode, formatNumber,
} from '../../../utils/formatters';
import { MANIFEST_STATUS_CONFIG, ROLE_CONFIG } from '../../../utils/manifestTrackingReportConfig';

const CheckBadge = ({ registered, photo, label }) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip
            size="small"
            label={label}
            color={registered ? 'success' : 'default'}
            variant={registered ? 'filled' : 'outlined'}
            sx={{ fontWeight: 800 }}
        />
        {photo && <Chip size="small" label="📷" variant="outlined" />}
    </Stack>
);

const ManifestTrackingTable = ({ rows = [], onOpenDetail }) => {
    if (rows.length === 0) {
        return <EmptyState text="No hay manifiestos con los filtros seleccionados." />;
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell><strong>Código</strong></TableCell>
                        <TableCell><strong>Fecha</strong></TableCell>
                        <TableCell><strong>Estado</strong></TableCell>
                        <TableCell><strong>Vehículo</strong></TableCell>
                        <TableCell><strong>Ruta</strong></TableCell>
                        <TableCell><strong>Conductor / Asistente</strong></TableCell>
                        <TableCell align="center"><strong>Check INI</strong></TableCell>
                        <TableCell align="center"><strong>Check FIN</strong></TableCell>
                        <TableCell align="right"><strong>Km</strong></TableCell>
                        <TableCell align="center"><strong>Ver</strong></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const cfg = MANIFEST_STATUS_CONFIG[row.manest] || {
                            label: row.manest, color: '#ccc', text: '#000',
                        };
                        const turnos = row.turnos || [];
                        const driver = turnos.find((t) => t.rol === 'DRIVER');
                        const assistant = turnos.find((t) => t.rol === 'ASSISTANT');
                        const paradas = row.paradas || [];
                        const totalVisitas = paradas.filter((p) => p.visitada).length;

                        return (
                            <TableRow key={row.manid} hover>
                                <TableCell>
                                    <Typography fontWeight={900}>
                                        {formatManifestCode(row.manid)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {row.pronom || '-'}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Stack spacing={0.2}>
                                        <Typography variant="body2">{formatDate(row.manfec)}</Typography>
                                        {row.manfecfin && (
                                            <Typography variant="caption" color="text.secondary">
                                                → {formatDate(row.manfecfin)}
                                            </Typography>
                                        )}
                                    </Stack>
                                </TableCell>

                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={cfg.label}
                                        sx={{ bgcolor: cfg.color, color: cfg.text, fontWeight: 800 }}
                                    />
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" fontWeight={700}>
                                        {row.vehmat || '-'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {row.vehdes || row.vehmar || ''}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                        {row.manori?.split(',')[0] || '-'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: 'block' }}>
                                        → {row.mandes?.split(',')[0] || '-'}
                                    </Typography>
                                    {paradas.length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            📍 {totalVisitas}/{paradas.length} visitadas
                                        </Typography>
                                    )}
                                </TableCell>

                                <TableCell>
                                    {driver ? (
                                        <Stack spacing={0.2}>
                                            <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 160 }}>
                                                {driver.empleado_nombre || `#${driver.emp_id}`}
                                            </Typography>
                                            {assistant && (
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                                                    Asist: {assistant.empleado_nombre || `#${assistant.emp_id}`}
                                                </Typography>
                                            )}
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Sin conductor
                                        </Typography>
                                    )}
                                </TableCell>

                                <TableCell align="center">
                                    <CheckBadge
                                        registered={Boolean(row.check_ini_id)}
                                        photo={Boolean(row.check_ini_foto)}
                                        label={row.check_ini_id ? 'OK' : '—'}
                                    />
                                </TableCell>

                                <TableCell align="center">
                                    <CheckBadge
                                        registered={Boolean(row.check_fin_id)}
                                        photo={Boolean(row.check_fin_foto)}
                                        label={row.check_fin_id ? 'OK' : '—'}
                                    />
                                </TableCell>

                                <TableCell align="right">
                                    <Typography fontWeight={800}>
                                        {row.km_recorridos != null ? formatNumber(row.km_recorridos) : '—'}
                                    </Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <Tooltip title="Ver detalle">
                                        <IconButton size="small" onClick={() => onOpenDetail(row.manid)}>
                                            <VisibilityIcon sx={{ color: '#009688' }} fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ManifestTrackingTable;